// @ts-strict-ignore
import lowerFirst from 'lodash/lowerFirst';
import upperFirst from 'lodash/upperFirst';
import moment from 'moment-timezone';

import {ordinalizeNumber} from 'common/util';
import {getCurrentMoment} from 'common/util/date';
import {pluralize} from 'common/util/string';
import {LocalizationStore} from 'common/i18n/store';
import {getRetentionBucketName, getRetentionBucketStartAndEnd} from 'common/report/retention/util';
import {BirthIntervalUnit} from 'common/report/retention/util/types';
import {NON_INTERVALIZED_VALUE} from 'common/report/retention/util/types';
import {DateRange} from 'common/widgets/date-range-picker/models/date-range';
import {getDateRangeLabel} from 'common/widgets/date-range-picker-new/util';
import {BookmarkDateRange, TimeUnit} from 'common/widgets/date-range-picker/models/types';
import {SegmentLabelCreator} from 'common/widgets/user-sidebar/sidebar-user-list/types';
import {
  isCohortSegment,
  isDateSegment,
  isNumericSegment,
  Segment,
} from 'common/query-builder/query-block/segment-menu/types';
import {CohortDetail} from 'common/widgets/create-cohort-button';
import {ButtonOption} from 'common/components/popover-card/types';
import {CohortType, Retention} from 'common/report/retention/models/types';
import {openUserSidebar} from 'common/widgets/user-sidebar/utils';
import {ReportName} from 'common/report/constants';
import {ConstantPropertyMapper} from 'common/report/util/properties';
import {InsightsQueryResults} from 'common/report/insights/util/types';
import {
  ChartType,
  InsightsBookmarkParams,
  RetentionAlignmentType,
  RetentionType,
  RetentionUnboundedModeType,
} from 'common/types/reports/bookmark';
import {Translatable} from 'common/i18n';

import {createCohort} from './util';

export interface TableAttrs {
  matchingShowClauseIndex: number;
  bornEventName: string;
  bucketSizes: number[];
  dataGroupName: string;
  dataGroupNamePlural: string;
  eventName: string;
  groupBy: Segment[];
  unboundedMode: RetentionUnboundedModeType;
  isCumulative: boolean;
  dateUnit: BirthIntervalUnit;
  dateRange: BookmarkDateRange;
  retentionUnit: TimeUnit;
  retentionType: RetentionType;
  retentionAlignmentType: RetentionAlignmentType;
  chartType: ChartType;
  projectTimezone: string;
  metricLabel: string;
  bookmark: InsightsBookmarkParams;
  queryResults: InsightsQueryResults<Retention>;
}

export interface Cell {
  absoluteValue: string;
  absoluteDropOffValue: string;
  cohortSize: number;
  columnIdx: number;
  rowIdx: number;
  groupedRowIdx?: number;
  completeDate: string | false;
  enteredValue: string;
  enteredDate?: Date;
  incomplete: boolean;
  isAggregated: boolean;
  propertyAggregation: string;
}

export enum ViewUsersType {
  Retain = `view-users-retain`,
  DropOff = `view-users-drop-off`,
}

export interface RetentionPopoverActionParams {
  action: ViewUsersType;
  color: string;
  popover: RetentionPopover;
}

export class RetentionPopover {
  private declare tableAttrs: TableAttrs;
  private declare cell: Cell;
  private declare i18n: LocalizationStore;

  constructor(tableAttrs: TableAttrs, cell: Cell, i18n: LocalizationStore) {
    this.tableAttrs = tableAttrs;
    this.cell = cell;
    this.i18n = i18n;
  }

  public isAggregatedCell() {
    return this.cell.isAggregated;
  }

  /**
   * Calculate position so that popover doesn't overflow bodyRect right or bottom.
   *
   * Assumes that the tableRect is fully visible.
   */
  public getTablePopoverPosition(elRect: DOMRect, tableRect: DOMRect, bodyRect: DOMRect) {
    const estWidth = 300;
    const estHeight = 350;
    const xSpacing = 10;

    let x = -tableRect.x,
      y = -tableRect.y;

    if (elRect.x + elRect.width + estWidth + xSpacing > bodyRect.width) {
      x += elRect.x - estWidth;
    } else {
      x += elRect.x + elRect.width + xSpacing;
    }

    if (elRect.y + estHeight > bodyRect.bottom) {
      y += bodyRect.bottom - estHeight;
    } else {
      y += elRect.y;
    }

    return {x, y};
  }

  public getTitle() {
    let title: Translatable = ``;
    if (this.tableAttrs.retentionType === `addiction`) {
      title = `Frequency: ${this.cell.columnIdx + 2} ${this.tableAttrs.dateUnit === `day` ? `hours` : `days`}`;
    } else {
      title = this.i18n.print(
        getRetentionBucketName({
          bucketIndex: this.cell.columnIdx,
          retentionType: this.tableAttrs.retentionType,
          retentionUnit: this.tableAttrs.retentionUnit,
          dateUnit: this.tableAttrs.dateUnit,
          bucketSizes: this.tableAttrs.bucketSizes,
        }),
      );
    }

    return `${title} / ${this.getEnteredValue()}`;
  }

  public getSubtitle() {
    const dataGroup = this.tableAttrs.dataGroupNamePlural;
    const dgContext = dataGroup === `users` ? `users` : undefined;

    if (this.isAggregatedCell()) {
      if (this.cell.completeDate) {
        return this.i18n.t(`chart-actions.retention.description-incomplete`, {
          context: dgContext,
          group: dataGroup,
        });
      }
      return this.i18n.t(`chart-actions.retention.description`, {
        context: dgContext,
        group: dataGroup,
      });
    } else if (this.isSegmented()) {
      return this.i18n.t(`chart-actions.retention.entered-with`, {
        context: dgContext,
        group: dataGroup,
        segment: this.cell.enteredValue,
      });
    }
    return this.i18n.t(`chart-actions.retention.entered-on`, {
      context: dgContext,
      group: dataGroup,
      point: this.cell.enteredValue,
    });
  }

  public isSegmented() {
    return this.tableAttrs.groupBy.length > 0;
  }

  public getRetainedCount() {
    return this.cell.absoluteValue;
  }

  public getDataGroupName() {
    return this.tableAttrs.dataGroupName;
  }

  public getDataGroupNamePlural() {
    return this.tableAttrs.dataGroupNamePlural;
  }

  public getDropOffCount() {
    return this.cell.absoluteDropOffValue;
  }

  public getEnteredValue() {
    return this.cell.enteredValue;
  }

  public getMetricLabel() {
    return this.tableAttrs.metricLabel;
  }

  public getCohortDisableMessage() {
    let tooltip = ``;
    if (this.tableAttrs.groupBy.some(isCohortSegment)) {
      tooltip = `To create a cohort please select all ${this.tableAttrs.dataGroupNamePlural} in the query`;
    } else if (this.tableAttrs.groupBy.some(isNumericSegment)) {
      tooltip = `Creating cohorts is disabled when breaking down by a numeric property`;
    } else if (this.cell.absoluteValue === `0` && this.cell.absoluteDropOffValue === `0`) {
      tooltip = `Cohorts can be created when there is at least 1 eligible ${this.tableAttrs.dataGroupName}`;
    } else if (this.tableAttrs.groupBy.some(isDateSegment)) {
      tooltip = `Creating cohorts is disabled when breaking down by a date property`;
    }
    return tooltip;
  }

  public getSegmentLabelCreator(type: CohortType): SegmentLabelCreator {
    const retained = type === CohortType.SpecificIntervalRetained;
    if (this.cell.isAggregated) {
      if (this.cell.completeDate) {
        return retained
          ? ({context, count, group}) =>
              ({t}) =>
                t(`view-users.retained-any-interval`, {context, count, group})
          : ({context, count, group}) =>
              ({t}) =>
                t(`view-users.dropped-any-interval`, {context, count, group});
      }
      return retained
        ? ({context, count, group}) =>
            ({t}) =>
              t(`view-users.retained-complete-interval`, {context, count, group})
        : ({context, count, group}) =>
            ({t}) =>
              t(`view-users.dropped-complete-interval`, {context, count, group});
    }
    // can change?
    const date = formatDate(this.cell.enteredDate, this.tableAttrs.projectTimezone);
    const dataGroup = this.tableAttrs.dataGroupName;
    const dgContext = dataGroup === `user` ? `user` : undefined;
    if (this.tableAttrs.chartType === ChartType.RetentionTrend) {
      return retained
        ? () =>
            this.i18n.t(`view-users.retained-date-unit`, {
              date,
              context: dgContext,
              group: dataGroup,
              dateUnit: this.tableAttrs.dateUnit,
            })
        : () =>
            this.i18n.t(`view-users.dropped-date-unit`, {
              date,
              context: dgContext,
              group: dataGroup,
              dateUnit: this.tableAttrs.dateUnit,
            });
    }
    return retained
      ? () => this.i18n.t(`view-users.retained-date`, {date, context: dgContext, group: dataGroup})
      : () => this.i18n.t(`view-users.dropped-date`, {date, context: dgContext, group: dataGroup});
  }

  public getSegments() {
    if (this.isSegmented()) {
      const enteredValue = this.getEnteredValue();
      // convert the segment value to its original value
      const segments = enteredValue.split(` / `).map((segment: string) => {
        return ConstantPropertyMapper[segment] || segment;
      });

      return segments;
    }
    return [];
  }

  private getViewUserDisableMessage(type: CohortType) {
    let tooltip = ``;
    const dataGroup = this.tableAttrs.dataGroupNamePlural;
    const dgContext = dataGroup === `users` ? `users` : undefined;
    if (this.tableAttrs.groupBy.some(isCohortSegment)) {
      tooltip = this.i18n.t(`chart-actions.retention.disabled.view-cohort`, {
        context: dgContext,
        group: this.tableAttrs.dataGroupNamePlural,
      });
    } else if (this.tableAttrs.groupBy.some(isNumericSegment)) {
      tooltip = this.i18n.t(`chart-actions.retention.disabled.view-numeric-segment`, {
        context: dgContext,
        group: this.tableAttrs.dataGroupNamePlural,
      });
    } else if (
      (this.cell.absoluteValue === `0` && type === CohortType.SpecificIntervalRetained) ||
      (this.cell.absoluteDropOffValue === `0` && type === CohortType.SpecificIntervalDropOff)
    ) {
      tooltip = this.i18n.t(`chart-actions.retention.disabled.no-segment`, {
        context: dgContext,
        group: this.tableAttrs.dataGroupNamePlural,
      });
    }
    return tooltip;
  }

  public getButtonOptions(): ButtonOption[] {
    const buttonOptions = [];
    const dataGroup = this.tableAttrs.dataGroupNamePlural;
    const dgContext = dataGroup === `users` ? `users` : undefined;
    buttonOptions.push({
      label: this.i18n.t(`chart-actions.retention.view-retained`, {
        context: dgContext,
        group: this.tableAttrs.dataGroupNamePlural,
      }),
      value: ViewUsersType.Retain,
      isDisabled: !!this.getViewUserDisableMessage(CohortType.SpecificIntervalRetained),
      tooltip: this.getViewUserDisableMessage(CohortType.SpecificIntervalRetained),
    });
    buttonOptions.push({
      label: this.i18n.t(`chart-actions.retention.view-dropped-off`, {
        context: dgContext,
        group: this.tableAttrs.dataGroupNamePlural,
      }),
      value: ViewUsersType.DropOff,
      isDisabled: !!this.getViewUserDisableMessage(CohortType.SpecificIntervalDropOff),
      tooltip: this.getViewUserDisableMessage(CohortType.SpecificIntervalDropOff),
    });
    return buttonOptions;
  }

  public getCohortDetails(): CohortDetail[] {
    const warning = this.cell.incomplete
      ? `${upperFirst(this.tableAttrs.dataGroupNamePlural)} will continue to qualify until the end of ${
          this.cell.completeDate
        }`
      : ``;

    let reportType = `retention`;
    if (this.tableAttrs.retentionType === `addiction`) {
      const frequencyCriteria = this.tableAttrs.isCumulative ? `cumulative` : `non-cumulative`;
      reportType = `addiction-${frequencyCriteria}`;
    }

    return [CohortType.SpecificIntervalRetained, CohortType.SpecificIntervalDropOff].map((type) => ({
      warning,
      description: this.getCohortDescription(type),
      label: this.getLabelForCohortDetail(type),
      type,
      cohort: createCohort({
        name: this.getCohortName(type),
        description: this.getCohortDescription(type),
        matchingShowClauseIdx: this.tableAttrs.matchingShowClauseIndex,
        bookmark: this.tableAttrs.bookmark,
        queryResults: this.tableAttrs.queryResults,
        behaviorType: type,
        columnIdx: this.cell.columnIdx,
        segments: this.getSegments(),
        groupedRowIdx: this.cell.groupedRowIdx,
        isSegmented: this.isSegmented(),
        reportType,
      }),
    }));
  }

  private getLabelForCohortDetail(type: CohortType) {
    if (type === CohortType.SpecificIntervalRetained) {
      return this.isAggregatedCell()
        ? `All retained ${this.tableAttrs.dataGroupNamePlural} that contribute to average`
        : `Retained ${this.tableAttrs.dataGroupNamePlural}`;
    }
    return this.isAggregatedCell()
      ? `All dropped ${this.tableAttrs.dataGroupNamePlural} that contribute to average`
      : `Dropped ${this.tableAttrs.dataGroupNamePlural}`;
  }

  private getCohortName(behaviorType: CohortType) {
    if (this.tableAttrs.retentionType === `addiction`) {
      const dateUnit = this.tableAttrs.dateUnit === `day` ? `hours` : `days`;
      const userCohortType = behaviorType === CohortType.SpecificIntervalDropOff ? `Dropped Users` : `Retained Users`;
      return `[Frequency] ${this.cell.columnIdx + 2} ${dateUnit} ${userCohortType}`;
    }

    const bucketName = this.i18n.print(
      getRetentionBucketName({
        bucketIndex: this.cell.columnIdx,
        retentionType: this.tableAttrs.retentionType,
        retentionUnit: this.tableAttrs.retentionUnit,
        dateUnit: this.tableAttrs.dateUnit,
        bucketSizes: this.tableAttrs.bucketSizes,
      }),
    );
    const userCohortType = behaviorType === CohortType.SpecificIntervalDropOff ? `Dropped Users` : `Retained Users`;
    return `[Retention] ${bucketName} ${userCohortType}`;
  }

  private getCustomBucketTitle(unboundedMode: RetentionUnboundedModeType) {
    const customBucketRange = getRetentionBucketStartAndEnd({
      bucketIndex: this.cell.columnIdx,
      bucketSizes: this.tableAttrs.bucketSizes,
    });

    const EQUALITY_OPERATORS = {
      none: ``,
      null: ``,
      undefined: ``,
      carry_back: `≥`,
      carry_forward: `≤`,
      consecutive_forward: ``,
    };

    return `${EQUALITY_OPERATORS[unboundedMode]}${
      customBucketRange.start === customBucketRange.end || unboundedMode !== `none`
        ? `${customBucketRange.start}`
        : `${customBucketRange.start}-${customBucketRange.end}`
    } ${pluralize(this.tableAttrs.retentionUnit, customBucketRange.end)} later`;
  }

  public getCohortDescription(behaviorType: CohortType) {
    if (this.tableAttrs.retentionType === `addiction`) {
      // To do: Return a fully formatted string for addiction once we add Create
      // Cohort functionality for the Frequency Report.

      // Description example:
      // Users who did Event A at least 2 hours in a day in the last 3 months.

      const groupName = upperFirst(this.tableAttrs.dataGroupNamePlural);
      return `${groupName} who did "${this.tableAttrs.eventName}" at least `;
    }

    const dateUnit = this.tableAttrs.dateUnit;
    const groupName = upperFirst(this.tableAttrs.dataGroupNamePlural);

    let retentionLabel: string;
    const {retentionUnit} = this.tableAttrs;
    if (this.tableAttrs.unboundedMode !== undefined && this.tableAttrs.unboundedMode !== `none`) {
      if (this.cell.columnIdx === 0) {
        retentionLabel = `any time later`;
      } else {
        retentionLabel = this.tableAttrs.bucketSizes.length
          ? this.getCustomBucketTitle(this.tableAttrs.unboundedMode)
          : `≥${this.cell.columnIdx} ${pluralize(retentionUnit, this.cell.columnIdx)} later`;
      }
    } else {
      if (this.cell.columnIdx === 0) {
        retentionLabel = `within 1 ${retentionUnit}`;
      } else {
        retentionLabel = this.tableAttrs.bucketSizes.length
          ? this.getCustomBucketTitle(`none`)
          : `${this.cell.columnIdx}-${this.cell.columnIdx + 1} ${retentionUnit}s later`;
      }
    }

    const dateRange = DateRange.fromBookmarkFormat(this.tableAttrs.dateRange);
    const dateRangeLabel = lowerFirst(
      this.i18n.print(
        getDateRangeLabel({
          dateRange,
          matchingPreset: null,
          projectAsOfDate: null,
          now: getCurrentMoment({localized: true}),
          formatMode: `sentence`,
        }),
      ),
    );
    let birthIntervalLabel: string;
    if (dateUnit === NON_INTERVALIZED_VALUE) {
      birthIntervalLabel = dateRangeLabel;
    } else if (!Number.isFinite(this.cell.groupedRowIdx)) {
      // average row
      birthIntervalLabel = `during any ${dateUnit} ${dateRangeLabel}`;
    } else if (dateRange.isRelativeWindow() || dateRange.isRolling()) {
      const birthIntervalIdx = this.cell.groupedRowIdx;
      birthIntervalLabel = `on the ${ordinalizeNumber(birthIntervalIdx + 1)} ${dateUnit} ${dateRangeLabel}`;
    } else {
      const formattedEnteredDate = formatDate(this.cell.enteredDate, this.tableAttrs.projectTimezone);
      if (dateUnit === TimeUnit.Day) {
        birthIntervalLabel = `on ${formattedEnteredDate}`;
      } else {
        birthIntervalLabel = `during the ${dateUnit} of ${formattedEnteredDate}`;
      }
    }

    return [
      `${groupName} who did`,
      `"${this.tableAttrs.bornEventName}"`,
      ...(this.isSegmented() ? [`with ${this.cell.enteredValue}`] : []),
      birthIntervalLabel,
      behaviorType === CohortType.SpecificIntervalDropOff ? `and then did not do` : `and then did`,
      `"${this.tableAttrs.eventName}"`,
      retentionLabel,
    ].join(` `);
  }

  public hideCohortFunctionality() {
    // We don't have QueryAPI support for cohort create in addiction yet.
    return this.tableAttrs.retentionType === `addiction`;
  }

  public getPropertyAggregation() {
    return this.cell.propertyAggregation;
  }
}

function formatDate(date: Date, timezone: string): string {
  return moment.tz(date, timezone).format(`MMM D, YYYY`);
}

export function handlePopoverAction({
  action,
  color,
  popover,
  updateUserSidebarContextFn,
}: {
  action: ViewUsersType;
  color: string;
  popover: RetentionPopover;
  updateUserSidebarContextFn: (isOpen: boolean) => void;
}): void {
  if ([ViewUsersType.Retain, ViewUsersType.DropOff].includes(action)) {
    const cohortDetails = popover.getCohortDetails();
    const type =
      action === ViewUsersType.Retain ? CohortType.SpecificIntervalRetained : CohortType.SpecificIntervalDropOff;
    const cohortDetail = cohortDetails.find((c) => c.type === type);
    openUserSidebar({
      hideCohortFunctionality: popover.hideCohortFunctionality(),
      includeAllUsers: true,
      reportName: ReportName.Retention,
      segments: [popover.getTitle()],
      dataGroupName: popover.getDataGroupName(),
      color,
      cohort: cohortDetail.cohort,
      cohortDetails: [cohortDetail],
      createSegmentLabel: popover.getSegmentLabelCreator(type),
    })
      .then(() => {
        updateUserSidebarContextFn(true);
      })
      .catch(() => {
        updateUserSidebarContextFn(false);
      });
  } else {
    throw new Error(`unsupported action`);
  }
}
